import React, { useState } from 'react';
import { Trash, Warning } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';

const SUPER_ADMIN_EMAILS = ['admin@carloo.in'];
const SUPER_ADMIN_MOBILES = ['9894021299'];

export function isSuperAdmin(user) {
  if (!user || user.role !== 'admin') return false;
  const email = (user.email || '').trim().toLowerCase();
  const mobile = (user.mobile || '').trim();
  return SUPER_ADMIN_EMAILS.includes(email) || SUPER_ADMIN_MOBILES.includes(mobile);
}

/**
 * Hard-delete button visible ONLY to super admin (admin@carloo.in / 9894021299).
 * Requires the user to type the record name as a confirmation.
 * Props: masterType (e.g. "pilots"), recordId, recordName, onDeleted (callback)
 */
export default function SuperAdminDeleteButton({ masterType, recordId, recordName, onDeleted, size = 16 }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  if (!isSuperAdmin(user)) return null;

  const safeName = (recordName || recordId || '').toString();
  const matches = typed.trim().toLowerCase() === safeName.trim().toLowerCase();

  const handleDelete = async () => {
    if (!matches) {
      setError('Typed name does not match.');
      return;
    }
    setBusy(true); setError('');
    try {
      const { data } = await api.delete(`/admin/master/${masterType}/${recordId}/permanent`);
      setOpen(false); setTyped('');
      if (onDeleted) onDeleted(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Delete failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(true); setTyped(''); setError(''); }}
        className="p-2 hover:bg-[#FF3333]/10 text-[#FF3333] transition-colors"
        title="Permanently delete (super admin only)"
        data-testid={`super-delete-${masterType}-${recordId}`}
      >
        <Trash size={size} weight="bold" />
      </button>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setTyped(''); setError(''); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#FF3333]">
              <Warning size={20} weight="fill" /> Permanently Delete
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="bg-[#FF3333]/5 border border-[#FF3333]/20 p-3">
              <p className="text-sm text-[#FF3333]">
                This will hard-delete <strong>{safeName}</strong> from the database. This action <strong>cannot be undone</strong>.
              </p>
              <p className="text-xs text-[#666] mt-2">
                Linked login accounts (for pilots, booking staff, pickup users) will also be removed automatically.
              </p>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#666]">Type <strong className="text-[#0A0A0A]">{safeName}</strong> to confirm</label>
              <Input
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                className="rounded-none mt-1"
                placeholder={safeName}
                autoFocus
                data-testid="super-delete-confirm-input"
              />
            </div>
            {error && <p className="text-xs text-[#FF3333]">{error}</p>}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-none">Cancel</Button>
              <Button
                type="button"
                onClick={handleDelete}
                disabled={!matches || busy}
                className="bg-[#FF3333] text-white hover:bg-[#FF3333]/90 rounded-none disabled:opacity-50"
                data-testid="super-delete-confirm-btn"
              >
                {busy ? 'Deleting...' : 'Delete Permanently'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
